import React, {useState} from 'react';
import {v4 as uuid} from 'uuid';
import {useShallow} from 'zustand/react/shallow';
import {Box, InputAdornment, Stack, TextField} from '@mui/material';
import {
  Body,
  designSystemToken,
  Headline,
  Icon,
  Tooltip,
} from '@lightricks/react-design-system';
import useUiInteractionEvent from '@contentlab/hooks/analytics/use-interaction-event';
import AnalyticsService from '@/services/analytics/AnalyticsService';
import {
  CONTENT_STUDIO_PRICING_SOURCES,
  CONTENT_STUDIO_VIEW_NAMES,
  FLOW_NAMES,
} from '@/lib/analytics/analyticsConstants';
import {SESSION_ID_NOT_FOUND} from '@/lib/delta/deltaConstants.js';
import {handleContentStudioError} from '@/utils/contentStudioErrorHandling';
import translate from '@/utils/translate';
import {ROUTES} from '@/config/routesConstants';
import {calculateVideoTokensForAssets} from '@/views/content-studio/components/utils';
import {FEATURE_ID} from '@/views/content-studio/constants';
import resolveCreditAccessAction, {
  CREDIT_ACCESS_ACTION,
} from '@/views/content-studio/utils/resolveCreditAccessAction';
import DropdownButton from '@/components/dropdown-button';
import useCreateReformattingProject from '@/hooks/content-studio/mutations/use-create-reformatting-project';
import useNavigation from '@/hooks/use-navigation';
import contentStudioStore, {
  contentStudioActions,
  ErrorKind,
} from '@/stores/contentStudioStore';
import useOpenPricing, {OPEN_PRICING_TYPE} from '../../hooks/use-open-pricing';
import FeaturePage from '../feature-page/FeaturePage';
import FilterMenuItem from '../filter-button/FilterMenuItem';
import ManyToManyEditor from '../many-to-many-editor/ManyToManyEditor';
import ManyToManySubmitButton from '../many-to-many-editor/ManyToManySubmitButton';
import ManyToManyOptionDisplay from '../many-to-many-option-display/ManyToManyOptionDisplay';
import {useDefaultProjectName} from '../projects/utils';
import styles from './ReformattingPage.module.scss';
import aspectRatios from './const';

function ReformattingPage() {
  const {uiInteraction} = useUiInteractionEvent();
  const {navigate} = useNavigation();
  const openPricing = useOpenPricing();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [selectedAspectRatios, setSelectedAspectRatios] = useState<string[]>(
    []
  );
  const [projectName, setProjectName] = useState(
    useDefaultProjectName(translate('Resize'))
  );
  const {selectedAssets} = contentStudioStore(
    useShallow((state) => ({
      selectedAssets: state.selectedAssets,
    }))
  );
  const {mutateAsync: createReformattingProject} =
    useCreateReformattingProject();

  // Get the common aspect ratio if all videos have the same one
  const getCommonAspectRatio = (): string => {
    if (selectedAssets.length === 0) return '';

    const firstAspectRatio = selectedAssets[0].aspectRatio || '';
    const allSameAspectRatio = selectedAssets.every(
      (asset) => asset.aspectRatio === firstAspectRatio
    );

    return allSameAspectRatio ? firstAspectRatio : '';
  };

  const commonAspectRatio = getCommonAspectRatio();

  const isUploading = selectedAssets.some((asset) => asset.isUploading);

  const handleResizeClick = async () => {
    setIsSubmitting(true);
    try {
      // Calculate required credits by filtering out identity transformations
      const requiredCredits = selectedAssets.reduce((assetTokens, asset) => {
        const nonIdentityTransformations = selectedAspectRatios.filter(
          (targetAspectRatio) => asset.aspectRatio !== targetAspectRatio
        );
        const assetTokenCount = calculateVideoTokensForAssets([asset]);
        return (
          assetTokens + assetTokenCount * nonIdentityTransformations.length
        );
      }, 0);

      const creditAccessAction =
        await resolveCreditAccessAction(requiredCredits);

      if (creditAccessAction !== CREDIT_ACCESS_ACTION.NO_OP) {
        const openType =
          creditAccessAction === CREDIT_ACCESS_ACTION.REDIRECT_TO_PRICING
            ? OPEN_PRICING_TYPE.REDIRECT
            : OPEN_PRICING_TYPE.MODAL;
        openPricing(openType, CONTENT_STUDIO_PRICING_SOURCES.EDITOR_GENERATE);
        setIsSubmitting(false);
        return;
      }

      const interactionId = uuid();
      const sessionId = AnalyticsService.getSessionId() || SESSION_ID_NOT_FOUND;

      uiInteraction({
        item_name: 'scale',
        interaction_location: CONTENT_STUDIO_VIEW_NAMES.REFORMAT_EDITOR,
        flow_name: FLOW_NAMES.CONTENT_STUDIO.REFORMAT,
        interaction_id: interactionId,
      });

      // Prepare reformats data with one entry per asset and aspect ratio combination
      // Skip identity transformations (where asset aspect ratio equals target aspect ratio)
      const reformats = selectedAssets.flatMap((asset) =>
        selectedAspectRatios
          .filter((aspectRatio) => asset.aspectRatio !== aspectRatio)
          .map((aspectRatio) => ({
            asset_id: asset.id,
            output_aspect_ratio: aspectRatio,
          }))
      );

      await createReformattingProject({
        project_name: projectName,
        reformats,
        analytics_params: {
          interaction_id: interactionId,
          session_id: sessionId,
        },
      });
      contentStudioActions.setIsProjectShelfOpen(true);
      contentStudioActions.setSelectedAssets([]);
      navigate(ROUTES.CONTENT_LAB);
    } catch (error) {
      handleContentStudioError(
        error,
        ErrorKind.FAILURE,
        'Failed to create reformatting project'
      );
    }
    setIsSubmitting(false);
  };

  return (
    <FeaturePage featureId={FEATURE_ID.RESIZE}>
      <ManyToManyEditor
        flowName={FLOW_NAMES.CONTENT_STUDIO.REFORMAT}
        asset_page_route={ROUTES.CONTENT_LAB_REFORMAT_ASSETS}
        isLoading={isSubmitting}
      >
        <Stack
          paddingX="36px"
          paddingTop="24px"
          direction="column"
          height="100%"
        >
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            marginBottom="8px"
          >
            <Headline size="xl">{translate('Resize to Any Ratio')}</Headline>
            <Icon name="Actions-Expand" appearance="primary" size="large" />
          </Stack>
          <Body size="lg" marginBottom="42px">
            {translate(
              'Automatically create multiple versions of your video content'
            )}
          </Body>
          <Stack
            direction="row"
            marginBottom="40px"
            justifyContent="space-between"
          >
            <DropdownButton
              closeAfterSelect={false}
              buttonClassName={styles.dropdownButton}
              mode="filled"
              size="medium"
              iconSize="small"
              caretColor={designSystemToken('semantic.fg.inverse')}
              label={translate('Choose aspect ratios')}
              options={
                aspectRatios?.map((aspectRatio) => ({
                  id: aspectRatio,
                  label: aspectRatio,
                  disabled: aspectRatio === commonAspectRatio,
                })) || []
              }
              selectedOptionIds={selectedAspectRatios}
              onChange={(option) => {
                if (option.disabled) return;

                let currentlySelectedOptions = [...selectedAspectRatios];
                if (currentlySelectedOptions.includes(option.id)) {
                  currentlySelectedOptions = currentlySelectedOptions.filter(
                    (aspectRatio) => aspectRatio !== option.id
                  );
                } else {
                  currentlySelectedOptions.push(option.id);
                }
                uiInteraction({
                  item_name: 'choose_aspect_ratio',
                  interaction_location:
                    CONTENT_STUDIO_VIEW_NAMES.REFORMAT_EDITOR,
                  flow_name: FLOW_NAMES.CONTENT_STUDIO.REFORMAT,
                  interaction_id: uuid(),
                  interaction_details: JSON.stringify({
                    aspect_ratio: currentlySelectedOptions.join(','),
                  }),
                });
                setSelectedAspectRatios(currentlySelectedOptions);
              }}
              renderMenuItem={(option) => {
                const tooltipMessage = option.disabled
                  ? translate('already in aspect ratio', {
                      count: selectedAssets.length,
                    })
                  : '';

                return (
                  <Tooltip
                    title={tooltipMessage}
                    arrow
                    type="light"
                    placement="right"
                  >
                    <Box
                      sx={{
                        'pointerEvents': option.disabled ? 'auto' : 'inherit',
                        'cursor': option.disabled ? 'not-allowed' : 'inherit',
                        'width': '100%',
                        'display': 'flex',
                        '& .MuiCheckbox-root': {
                          cursor: option.disabled
                            ? 'not-allowed !important'
                            : 'inherit',
                        },
                      }}
                    >
                      <FilterMenuItem
                        option={option}
                        isSelected={selectedAspectRatios.includes(option.id)}
                      />
                    </Box>
                  </Tooltip>
                );
              }}
            />
          </Stack>
          <ManyToManyOptionDisplay
            options={selectedAspectRatios.map((aspectRatio) => ({
              id: aspectRatio,
              label: aspectRatio,
            }))}
            onDelete={(option) =>
              setSelectedAspectRatios(
                selectedAspectRatios.filter((id) => id !== option)
              )
            }
          />
          <Stack
            direction="row"
            justifyContent="space-between"
            marginTop="auto"
          >
            <TextField
              variant="outlined"
              label={translate('Insert project name')}
              placeholder={translate('Enter a project name')}
              value={projectName}
              onChange={(e) => setProjectName(e.target.value)}
              sx={{marginBottom: '60px'}}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Icon
                      name="Navigation-Edit"
                      size="medium"
                      appearance="primary"
                    />
                  </InputAdornment>
                ),
              }}
            />
            <ManyToManySubmitButton
              selectedAssets={selectedAssets}
              selectedOptions={selectedAspectRatios.length}
              verb="Resize"
              noun="format"
              iconName="Actions-Expand"
              onClick={handleResizeClick}
              disabled={
                selectedAssets.length === 0 ||
                selectedAspectRatios.length === 0 ||
                selectedAssets.some((asset) => asset.isUploading) ||
                isSubmitting
              }
              isLoading={isSubmitting || isUploading}
              bottonTooltip={
                isUploading
                  ? translate('files-uploading-tooltip', {
                      featureName: 'Resize',
                    })
                  : ''
              }
            />
          </Stack>
        </Stack>
      </ManyToManyEditor>
    </FeaturePage>
  );
}

export default ReformattingPage;
